import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Icon from "../../../components/common/Icon";
import Button from "../../../components/common/Button";
import Reveal from "../../../components/common/Reveal";
import { getPublicPostsApi } from "../../../api/content.api";

function getItems(response) {
  const value = response?.data?.data;

  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.posts)) return value.posts;
  if (Array.isArray(value?.results)) return value.results;

  return [];
}

function getImageUrl(path) {
  if (!path) return null;

  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("blob:")
  ) {
    return path;
  }

  const base =
    process.env.REACT_APP_API_BASE_URL?.replace("/api/v1", "") || "";

  return `${base}${path}`;
}

function getTitle(post) {
  return (
    post?.title ||
    post?.name ||
    post?.headline ||
    "عنوان خبر"
  );
}

function getSummary(post) {
  return (
    post?.excerpt ||
    post?.summary ||
    post?.description ||
    post?.content ||
    "جزئیات این خبر را در صفحه خبر مشاهده کنید."
  );
}

function getCategory(post) {
  return (
    post?.category?.name ||
    post?.categoryName ||
    post?.type ||
    "اخبار"
  );
}

function getDate(post) {
  const value =
    post?.publishedAt ||
    post?.publishDate ||
    post?.createdAt ||
    post?.date;

  if (!value) return "تاریخ نامشخص";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("fa-IR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getPostImage(post) {
  return ( post?.coverImage || post?.image 
    || post?.cover || post?.thumbnail 
    || post?.featuredImage || post?.imageUrl 
    || post?.media?.url || null ); }

function getPostLink(post) {
  const id = post?.id || post?._id || post?.slug;

  if (!id) return "/news";

  return `/news/${id}`;
}

function NewsCard({ post, featured = false }) {
  const image = getImageUrl(getPostImage(post));
  const title = getTitle(post);
  const summary = getSummary(post);
  const category = getCategory(post);
  const date = getDate(post);
  const link = getPostLink(post);

  return (
    <Link
      to={link}
      className={[
        "glass group relative block overflow-hidden rounded-[2rem] transition-all duration-500",
        "hover:-translate-y-1.5 hover:shadow-card-hover",
        featured ? "lg:row-span-2" : "",
      ].join(" ")}
    >
      <div
        className={[
          "relative overflow-hidden bg-slate-100",
          featured
            ? "h-[260px] sm:h-[300px] lg:h-full lg:min-h-[470px]"
            : "h-52",
        ].join(" ")}
      >
        {image ? (
          <img
            src={image}
            alt={title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-ink via-emerald-900 to-emerald-700">
            <div className="grid h-20 w-20 place-items-center rounded-3xl border border-white/10 bg-white/10 text-emerald-300 backdrop-blur-xl">
              <Icon name="news" className="h-9 w-9" />
            </div>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent opacity-80" />

        <div className="absolute right-4 top-4">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-black/25 px-3 py-1.5 text-[10px] font-black text-white backdrop-blur-md">
            <span className="h-1.5 w-1.5 rounded-full bg-floodlight" />
            {category}
          </span>
        </div>

        {featured && (
          <div className="absolute bottom-5 right-5 left-5">
            <div className="mb-3 flex items-center gap-2 text-[11px] font-medium text-white/65">
              <Icon name="calendar" className="h-3.5 w-3.5" />
              {date}
            </div>

            <h3 className="line-clamp-2 text-xl font-black leading-8 text-white sm:text-2xl">
              {title}
            </h3>
          </div>
        )}
      </div>

      {!featured && (
        <div className="p-5">
          <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400">
            <Icon name="calendar" className="h-3.5 w-3.5 text-emerald-500" />
            {date}
          </div>

          <h3 className="mt-3 line-clamp-2 text-base font-black leading-7 text-slate-900 transition-colors group-hover:text-emerald-600">
            {title}
          </h3>

          <p className="mt-2 line-clamp-2 text-xs leading-6 text-slate-500">
            {summary}
          </p>

          <div className="mt-4 flex items-center justify-between border-t border-slate-200/70 pt-4">
            <span className="text-[11px] font-black text-emerald-600">
              ادامه خبر
            </span>

            <span className="grid h-7 w-7 place-items-center rounded-full bg-white/80 text-slate-400 transition-all group-hover:bg-ink group-hover:text-floodlight">
              <Icon name="arrowLeft" className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      )}
    </Link>
  );
}

function SkeletonCard({ featured = false }) {
  return (
    <div
      className={[
        "glass animate-pulse overflow-hidden rounded-[2rem]",
        featured ? "lg:row-span-2" : "",
      ].join(" ")}
    >
      <div
        className={
          featured
            ? "h-[260px] bg-slate-100 sm:h-[300px] lg:h-full lg:min-h-[470px]"
            : "h-52 bg-slate-100"
        }
      />

      {!featured && (
        <div className="space-y-3 p-5">
          <div className="h-3 w-20 rounded bg-slate-100" />
          <div className="h-5 w-full rounded bg-slate-100" />
          <div className="h-4 w-4/5 rounded bg-slate-100" />
        </div>
      )}
    </div>
  );
}

export default function LatestNews() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    getPublicPostsApi({
      limit: 5,
      page: 1,
    })
      .then((response) => {
        if (!mounted) return;

        setPosts(getItems(response).slice(0, 5));
      })
      .catch(() => {
        if (mounted) {
          setPosts([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section className="relative overflow-hidden py-20 sm:py-24 lg:py-28">
      <div className="pointer-events-none absolute -right-40 top-20 h-96 w-96 rounded-full bg-emerald-400/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-40 bottom-10 h-96 w-96 rounded-full bg-lime-400/10 blur-3xl" />

      <div className="container-page relative">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-2xl">
            <span className="section-eyebrow">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
              آخرین اخبار و اطلاعیه‌ها
            </span>

            <h2 className="section-title mt-5">
              در جریان
              <span className="text-gradient"> فوتبال شهر</span>
              باشید
            </h2>

            <p className="mt-4 max-w-xl text-sm leading-8 text-slate-500 sm:text-base">
              تازه‌ ترین اخبار، اطلاعیه‌ها و رویدادهای فوتبال شهرستان پاوه را از
              مرجع رسمی سامانه دنبال کنید.
            </p>
          </div>

          <Button
            as={Link}
            to="/news"
            variant="outlineDark"
            size="sm"
            className="w-fit shrink-0"
          >
            مشاهده همه اخبار
            <Icon name="arrowLeft" className="h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="mt-12 grid gap-5 lg:grid-cols-2">
            <SkeletonCard featured />
            <div className="grid gap-5 sm:grid-cols-2">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>
        ) : posts.length === 0 ? (
          <div className="glass mt-12 rounded-[2rem] border-dashed px-6 py-20 text-center">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-slate-50 text-slate-300">
              <Icon name="news" className="h-8 w-8" />
            </div>

            <h3 className="mt-5 text-lg font-black text-slate-700">
              هنوز خبری منتشر نشده است
            </h3>

            <p className="mx-auto mt-2 max-w-md text-sm leading-7 text-slate-400">
              به‌ محض انتشار خبر یا اطلاعیه جدید، آخرین مطالب در این بخش
              نمایش داده خواهند شد.
            </p>

            <Button
              as={Link}
              to="/news"
              variant="outlineDark"
              size="sm"
              className="mt-6"
            >
              آرشیو اخبار
              <Icon name="arrowLeft" className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Reveal className="mt-14 grid gap-5 lg:grid-cols-2">
            <NewsCard post={posts[0]} featured />

            <div className="grid gap-5 sm:grid-cols-2">
              {posts.slice(1, 5).map((post) => (
                <NewsCard
                  key={post.id || post._id || post.slug}
                  post={post}
                />
              ))}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
